import React from "react";

import type { LandingPageContent, Locale } from "../content/types";
import {
  formatSekRange,
  serviceEstimateExampleContent,
  serviceEstimateExamples,
} from "../content/serviceEstimateExamples";
import { CardVisual } from "./CardVisual";
import { PricingEstimatorSection } from "./PricingEstimatorSection";

type EstimateSummaryProps = {
  content: LandingPageContent["estimate"];
  locale: Locale;
};

export function EstimateSummary({ content, locale }: EstimateSummaryProps) {
  const exampleContent = serviceEstimateExampleContent[locale];

  return (
    <section className="db-section section-band estimate-summary" id={content.id} aria-labelledby={`${content.id}-title`}>
      <div className="db-container">
        <div className="section-heading">
          <p className="section-eyebrow">{content.eyebrow}</p>
          <h2 id={`${content.id}-title`}>{content.title}</h2>
          <p>{content.lead}</p>
        </div>

        <div className="content-card-grid estimate-example-grid" aria-label={exampleContent.gridLabel}>
          {serviceEstimateExamples.map((servicePackage) => (
            <article
              className="content-card content-card--visual estimate-example-card"
              data-card-family="pricing-examples"
              data-service-package={servicePackage.servicePackage}
              key={servicePackage.key}
            >
              <CardVisual assetId={servicePackage.assetId} />
              <div className="estimate-example-card__copy">
                <h3>{servicePackage.title[locale]}</h3>
                <p>{servicePackage.body[locale]}</p>
              </div>
              <dl className="estimate-example-metrics">
                <div className="estimate-example-metrics__item">
                  <dt>{exampleContent.monthlyRangeLabel}</dt>
                  <dd>{formatSekRange(servicePackage.monthlyRangeSek, locale, "monthly", { compact: true })}</dd>
                </div>
                <div className="estimate-example-metrics__item">
                  <dt>{exampleContent.setupRangeLabel}</dt>
                  <dd>{formatSekRange(servicePackage.setupRangeSek, locale, "setup", { compact: true })}</dd>
                </div>
              </dl>
              <div className="estimate-example-signals" aria-label={exampleContent.signalsLabel}>
                <span>{exampleContent.signalsLabel}</span>
                <ul>
                  {servicePackage.signals[locale].map((signal) => (
                    <li key={signal}>{signal}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <ul className="estimate-notes">
          {content.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
          <li>{exampleContent.nonBindingNote}</li>
          <li>{exampleContent.creditUnitNote}</li>
        </ul>

        <PricingEstimatorSection locale={locale} />
      </div>
    </section>
  );
}
